import React, {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useReducer,
  useMemo,
  useState,
} from 'react';
import {
  useCategories,
  useCompaniesPublic,
  useCompanyEmployees,
  useGetLotStatuses,
  useRegions,
  useTenderTypes,
} from '@web-kc/core/api';
import { useDebouncedCallback } from 'use-debounce';
import { LotAndTenderFiltersContextI, SelectedFiltersT } from '../types';
import { contextDefaultValues, defaultFilters } from '../constants';
import { useRouter } from 'next/router';

export type Action =
  | {
      type: 'SET_FIELD';
      field: keyof SelectedFiltersT;
      value: SelectedFiltersT[keyof SelectedFiltersT];
    }
  | { type: 'RESET' }
  | { type: 'REPLACE'; value: SelectedFiltersT };

function filterReducer(
  state: SelectedFiltersT,
  action: Action,
): SelectedFiltersT {
  const preserved = Object.fromEntries(
    Object.entries(state).filter(([k]) => !(k in defaultFilters)),
  );

  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };

    case 'RESET':
      return { ...defaultFilters };

    case 'REPLACE':
      return { ...action.value, ...preserved };

    default:
      return state;
  }
}

export const LotAndTenderFiltersContext =
  createContext<LotAndTenderFiltersContextI>(contextDefaultValues);

export const LotAndTenderFiltersProvider: FC<
  PropsWithChildren<{
    initialFilters: SelectedFiltersT;
    onApply: (filters: SelectedFiltersT) => void;
  }>
> = ({ children, initialFilters, onApply }) => {
  const [selectedFilters, dispatch] = useReducer(filterReducer, initialFilters);
  const [keywordCategory, setKeywordCategory] = useState('');
  const [keywordCompany, setKeywordCompany] = useState('');

  const { pathname } = useRouter();
  const isCabinet = pathname.includes('/cabinet');

  const { data: lotTypes } = useTenderTypes();
  const { data: lotStatuses } = useGetLotStatuses();
  const { data: categories, isLoading: isLoadingCategories } = useCategories({
    keyword: keywordCategory,
  });
  const { data: regions } = useRegions();

  const { data: employees } = useCompanyEmployees(
    { page: 0, size: 100 },
    isCabinet,
  );

  const { data: companies, isLoading: isLoadingCompany } = useCompaniesPublic(
    {
      page: 0,
      size: 50,
      keyword: keywordCompany,
    },
    !isCabinet,
  );

  const debouncedSearchCategory = useDebouncedCallback((q: string) => {
    setKeywordCategory(q);
  }, 300);

  const debouncedSearchCompany = useDebouncedCallback((q: string) => {
    setKeywordCompany(q);
  }, 300);

  const applyFilters = () => {
    onApply(selectedFilters);
  };

  const applyDirect = (next: SelectedFiltersT) => {
    onApply(next);
    dispatch({ type: 'REPLACE', value: next });
  };

  const setField = <K extends keyof SelectedFiltersT>(
    field: K,
    value: SelectedFiltersT[K],
  ) => {
    dispatch({ type: 'SET_FIELD', field, value });
  };

  const resetFilters = () => {
    dispatch({ type: 'RESET' });
  };

  const contextValue = useMemo(
    () => ({
      selectedFilters,
      setField,
      resetFilters,
      applyFilters,
      initialFilters,
      lotTypes,
      lotStatuses,
      categories,
      isLoadingCategories,
      debouncedSearchCategory,
      applyDirect,
      companies,
      isLoadingCompany,
      debouncedSearchCompany,
      employees,
      regions,
    }),
    [
      selectedFilters,
      initialFilters,
      categories,
      companies,
      regions,
      lotTypes,
      lotStatuses,
      employees,
    ],
  );

  return (
    <LotAndTenderFiltersContext.Provider value={contextValue}>
      {children}
    </LotAndTenderFiltersContext.Provider>
  );
};

export const useFilters = (): LotAndTenderFiltersContextI => {
  const ctx = useContext(LotAndTenderFiltersContext);
  if (!ctx) {
    throw new Error(
      'useFilters must be used within LotAndTenderFiltersProvider',
    );
  }
  return ctx;
};
